import { useId } from "react";
import { GitBranch } from "lucide-react";
import { labelColor } from "@/lib/cover";
import { cn } from "@web-shared/lib/utils";

/**
 * A seed's vinyl record — CSS-layered disc with grooves, conic reflection,
 * a cream label with curved text and a play button. Matches the extension's
 * browse carousel aesthetic. Size it with `className` on the outer wrapper.
 *
 * When `interactive` is true the component renders a play button in the label
 * center; otherwise just the visual (for feed cards where play is elsewhere).
 */
export function SeedCover({
  seed,
  spinning = false,
  interactive,
  playStyle = "label",
  onPlay,
  playing,
  remixBadge,
  className,
}: {
  seed: { id: string; title: string; genre: string | null; bpm: number | null; key: string | null; producer: string };
  spinning?: boolean;
  interactive?: boolean;
  /** "label": small green button inside the label (seed page). "overlay": large semi-transparent over the disc (feed cards). */
  playStyle?: "label" | "overlay";
  onPlay?: () => void;
  playing?: boolean;
  /**
   * Stamp this record as a remix, so it reads as one in a screenshot.
   *
   * The caller picks the shape because the component is sized entirely by
   * `className` (56px in a feed row, 480px on the seed page) and measuring
   * itself would mean a ResizeObserver per card. "pill" is for a record big
   * enough to carry it — the seed page's hero, a podium card. "pill-sm" is
   * the same stamp for the ~90px discs of the feed, where the full-size one
   * crowds the play button. Below ~72px only "pip" fits. All are aria-hidden
   * — every surface that shows one also says it in words.
   */
  remixBadge?: "pip" | "pill-sm" | "pill";
  className?: string;
}) {
  const color = labelColor(seed.id);
  const uid = useId();
  const titleUp = seed.title.toUpperCase();
  const byLine = remixBadge ? `A REMIX BY ${seed.producer.toUpperCase()}` : `A SEED BY ${seed.producer.toUpperCase()}`;

  return (
    <div className={cn("relative aspect-square", className)}>
      {/* Glow behind the disc. A gradient, not `blur()`: a feed shows dozens
          of these, and a blur layer each (with the overlay's backdrop blur)
          ran iOS Safari out of graphics memory — the page painted black. */}
      <div
        className="pointer-events-none absolute -inset-[20%] transition-opacity duration-700"
        style={{ background: `radial-gradient(closest-side, ${color}, transparent)`, opacity: spinning ? 0.25 : 0.12 }}
      />

      {/* Disc body */}
      <div
        className={cn(
          "absolute inset-0 rounded-full bg-[#050505] overflow-hidden",
          "shadow-[0_16px_40px_rgba(0,0,0,0.7),inset_0_0_0_1px_rgba(255,255,255,0.08)]",
          spinning && "animate-[spin_8s_linear_infinite]",
        )}
      >
        {/* Grooves */}
        <div
          className="absolute inset-0 rounded-full opacity-80"
          style={{
            background: "repeating-radial-gradient(#111 0px, #151515 2px, #0a0a0a 3px, #111 4px)",
          }}
        />

        {/* Conic reflection. Plain alpha, not mix-blend-screen: over a disc
            this dark the two are indistinguishable, and a blend mode is one
            more composited layer per record. */}
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background: `conic-gradient(
              transparent 0deg,
              rgba(255,255,255,0.06) 30deg,
              rgba(255,255,255,0.12) 45deg,
              rgba(255,255,255,0.06) 60deg,
              transparent 90deg,
              transparent 180deg,
              rgba(255,255,255,0.06) 210deg,
              rgba(255,255,255,0.12) 225deg,
              rgba(255,255,255,0.06) 240deg,
              transparent 270deg
            )`,
          }}
        />

        {/* Subtle ring accents */}
        <div className="absolute inset-[15%] rounded-full border border-white/[0.04]" />
        <div className="absolute inset-[30%] rounded-full border border-white/[0.04]" />

        {/* Label — cream disc with curved text */}
        <div
          className={cn(
            "absolute inset-0 m-auto rounded-full border-[4px] border-[#111] flex items-center justify-center z-10",
            interactive && playStyle === "label" ? "w-[36%] h-[36%]" : "w-[40%] h-[40%]",
          )}
          style={{
            background: `color-mix(in srgb, ${color} 15%, #E8E3D2)`,
            boxShadow: "inset 0 0 12px rgba(0,0,0,0.4), 0 0 16px rgba(0,0,0,0.7)",
          }}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Top curve: seed title */}
            <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none" viewBox="0 0 200 200">
              <path id={`${uid}-top`} d="M 30 100 A 70 70 0 0 1 170 100" fill="transparent" />
              <text fontFamily="system-ui, sans-serif" fontWeight="700" fontSize="11" letterSpacing="2.5" fill="#1a1a1a">
                <textPath href={`#${uid}-top`} startOffset="50%" textAnchor="middle">{titleUp.slice(0, 20)}</textPath>
              </text>
            </svg>

            {/* Bottom curve: producer */}
            <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none" viewBox="0 0 200 200">
              <path id={`${uid}-btm`} d="M 170 100 A 70 70 0 0 1 30 100" fill="transparent" />
              <text fontFamily="system-ui, sans-serif" fontWeight="600" fontSize="8" letterSpacing="3" fill="#1a1a1a">
                <textPath href={`#${uid}-btm`} startOffset="50%" textAnchor="middle">{byLine.slice(0, 24)}</textPath>
              </text>
            </svg>

            {/* Play button (label style) or spindle */}
            {interactive && playStyle === "label" ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onPlay?.(); }}
                aria-label={playing ? `Pause ${seed.title}` : `Play ${seed.title}`}
                className="relative z-30 flex size-[44%] items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_0_24px_rgba(167,243,160,0.4)] transition-all hover:scale-105 hover:bg-white"
              >
                {playing ? (
                  <svg viewBox="0 0 24 24" className="size-[40%]" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
                ) : (
                  <svg viewBox="0 0 24 24" className="size-[40%]" fill="currentColor"><path d="M8 5.14v13.72a1 1 0 001.5.86l11-6.86a1 1 0 000-1.72l-11-6.86A1 1 0 008 5.14z" /></svg>
                )}
              </button>
            ) : (
              <div className="size-3 rounded-full bg-[#111]" />
            )}
          </div>
        </div>
      </div>

      {/* The remix stamp. A sibling of the disc body, or it would spin with the
          record; below the overlay's z-20 and pointer-events-none, because the
          whole disc has to stay the tap target. */}
      {(remixBadge === "pill" || remixBadge === "pill-sm") && (
        <span
          aria-hidden
          // Inside the disc, not overhanging it: the seed page's next section
          // stacks above the hero, and an overhanging pill was sliced in half
          // by it.
          className={cn(
            "pointer-events-none absolute left-1/2 z-10 -translate-x-1/2 rounded-full bg-primary font-bold uppercase text-primary-foreground shadow-[0_2px_10px_rgba(0,0,0,0.6)]",
            remixBadge === "pill"
              ? "bottom-[5%] px-2 py-0.5 text-[9px] tracking-wider"
              : "bottom-[6%] px-1.5 text-[7px] leading-[13px] tracking-wide",
          )}
        >
          ⑂ Remix
        </span>
      )}
      {remixBadge === "pip" && (
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-[2%] right-[2%] z-10 grid size-4 place-items-center rounded-full bg-primary text-primary-foreground ring-2 ring-background"
        >
          <GitBranch className="size-2.5" />
        </span>
      )}

      {/* Play / pause overlay — a 50% circle on the vinyl, but the whole disc
          is the button. On the phone's feed row the disc is 56px, which made
          that circle a 28px target: the thing you tap most on the page, at
          half the size a thumb needs. The circle is what you see; the disc is
          what you hit. */}
      {interactive && playStyle === "overlay" && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPlay?.(); }}
          aria-label={playing ? `Pause ${seed.title}` : `Play ${seed.title}`}
          className="group absolute inset-0 z-20 flex items-center justify-center rounded-full"
        >
          <span className="flex size-[50%] items-center justify-center rounded-full bg-black/40 text-foreground/90 transition-all group-hover:scale-105 group-hover:bg-black/55">
            {playing ? (
              <svg viewBox="0 0 24 24" className="size-[40%]" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" className="size-[40%] translate-x-[5%]" fill="currentColor"><path d="M8 5.14v13.72a1 1 0 001.5.86l11-6.86a1 1 0 000-1.72l-11-6.86A1 1 0 008 5.14z" /></svg>
            )}
          </span>
        </button>
      )}
    </div>
  );
}
